import { createFileRoute } from "@tanstack/react-router";
import { Fragment } from "react";
import { loadGuide } from "~/utils/content-fns";
import { seo, seoLinks, articleJsonLd, faqJsonLd } from "~/utils/seo";
import { TableOfContents } from "~/components/TableOfContents";
import { BlockRenderer } from "~/components/blocks";
import { AskAiSummary } from "~/components/AskAiSummary";
import { TEAM_AUTHOR_KEY, authorPath } from "~/utils/authors";
import type { AuthorView, GuideView } from "~/types/views";

/**
 * Named people become schema.org Person authors. The collective C1 byline is
 * left out so `articleJsonLd` falls back to crediting the Organization, which
 * is what "The C1 Team" actually means. Mirrors `blog/$slug.tsx`.
 *
 * The `url` points at the author's page so the Person resolves to a described
 * entity rather than a bare name string; outside contributors have no page, so
 * they stay nameless nodes.
 */
function personAuthors(authors: AuthorView[]) {
  const people = authors.filter((a) => a.key !== TEAM_AUTHOR_KEY);
  return people.length > 0
    ? people.map((a) => ({
        name: a.name,
        url: a.hasPage ? authorPath(a.key) : null,
      }))
    : undefined;
}

/**
 * One name in the byline, linked to the author's page when they have one.
 *
 * Outside contributors ("James Walker", "Sooter Saalu") have no record in
 * authors.json and the collective C1 byline is the organization rather than a
 * person, so both render as plain text rather than a link to a 404.
 */
function BylineName({ author, label }: { author: AuthorView; label: string }) {
  if (!author.hasPage) return <span>{label}</span>;
  return (
    <a
      href={authorPath(author.key)}
      className="underline decoration-gray-40 underline-offset-2 transition hover:text-teal-dark hover:decoration-teal-dark"
      rel="author"
    >
      {label}
    </a>
  );
}

/** "By Claire McKenna, Director of Corporate Marketing" / "By The C1 Team" */
function Byline({ authors }: { authors: AuthorView[] }) {
  // A sole author carries their title; with more than one the titles crowd the
  // line out, so multiple bylines are names only.
  const solo = authors.length === 1;
  return (
    <span>
      {"By "}
      {authors.map((a, i) => (
        <Fragment key={a.key}>
          {i > 0 && (i === authors.length - 1 ? " and " : ", ")}
          <BylineName author={a} label={solo && a.title ? `${a.name}, ${a.title}` : a.name} />
        </Fragment>
      ))}
    </span>
  );
}

/** Byline + publish date line under the guide title. */
function GuideMeta({ guide }: { guide: GuideView }) {
  const hasAuthors = guide.authors.length > 0;
  if (!hasAuthors && !guide.date) return null;
  return (
    <div className="mb-6 flex flex-wrap items-center gap-x-2 gap-y-1 font-body text-sm text-gray-60">
      {hasAuthors && <Byline authors={guide.authors} />}
      {hasAuthors && guide.date && <span aria-hidden="true">&middot;</span>}
      {guide.date && (
        <time dateTime={new Date(guide.date).toISOString().slice(0, 10)}>
          {new Date(guide.date).toLocaleDateString("en-US", { year: "numeric", month: "long", day: "numeric" })}
        </time>
      )}
    </div>
  );
}

export const Route = createFileRoute("/guides/$slug")({
  loader: ({ params }) => loadGuide({ data: { slug: params.slug } }),
  head: ({ loaderData }) => ({
    meta: loaderData
      ? [
          ...seo({
            title: loaderData.seo_title || loaderData.title,
            description: loaderData.seo_description || loaderData.description,
            image: loaderData.featured_image,
            url: `/guides/${loaderData.slug}`,
            noindex: loaderData.robots === "noindex",
          }),
        ]
      : [],
    links: loaderData ? seoLinks({ url: `/guides/${loaderData.slug}` }) : [],
    scripts:
      loaderData && loaderData.robots !== "noindex"
        ? [
            articleJsonLd({
              type: "Article",
              url: `/guides/${loaderData.slug}`,
              title: loaderData.title,
              description: loaderData.description,
              image: loaderData.featured_image,
              datePublished: loaderData.date,
              authors: personAuthors(loaderData.authors),
            }),
            faqJsonLd(loaderData.faq),
          ].filter((s): s is NonNullable<typeof s> => s !== null)
        : [],
  }),
  component: GuideSingle,
});

function GuideSingle() {
  const guide = Route.useLoaderData();

  // Block-based guide — render via BlockRenderer
  if (guide.layout === "blocks" && guide.blocks) {
    return <BlockRenderer blocks={guide.blocks} />;
  }

  // Standard markdown guide
  const hasToc = guide.toc.length > 0;

  return (
    <>
      {/* bg_swipe: diagonal SVG behind content — matches Hugo baseof.html */}
      {guide.bg_swipe && (
        <div className="relative inset-x-0 overflow-hidden">
          <div className="absolute inset-x-0 top-0 z-0">
            <svg width="1440" height="544" viewBox="0 0 1440 544" fill="none" aria-hidden>
              <path d="M0 0H1440V343L0 543.5V0Z" fill="#f7f7f5" />
            </svg>
          </div>
        </div>
      )}

      <div className="relative z-10 px-4 py-16 md:px-8">
        {hasToc ? (
          /* ---- Two-column layout: sidebar + content ---- */
          <article className="mx-auto max-w-screen-xl">
            <h1 className="mb-3 font-display text-4xl font-bold leading-tight text-charcoal md:text-5xl">
              {guide.title}
            </h1>
            <GuideMeta guide={guide} />
            {guide.featured_image && (
              <img
                src={guide.featured_image}
                alt={guide.title}
                className="mb-8 w-full rounded-lg"
              />
            )}

            <div className="flex flex-col md:flex-row md:gap-8 xl:gap-24">
              {/* Sidebar */}
              <div className="flex-none md:w-[17.18rem] md:pt-4">
                <div className="sticky md:top-28">
                  <AskAiSummary url={`/guides/${guide.slug}`} className="mb-6 hidden md:block" />
                  <TableOfContents entries={guide.toc} />
                  {guide.download && (
                    <div className="mt-8 hidden md:block">
                      <a
                        href={guide.download}
                        className="inline-flex items-center gap-2 rounded-md bg-teal-cta px-4 py-2 text-sm font-semibold text-white hover:bg-teal-cta-hover"
                        download
                      >
                        <svg className="h-4 w-4" fill="none" stroke="currentColor" strokeWidth={2} viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" d="M12 4v12m0 0l-4-4m4 4l4-4M4 18h16" />
                        </svg>
                        Download PDF
                      </a>
                    </div>
                  )}
                </div>
              </div>

              {/* Article body */}
              <div
                className={`content group flex-1 text-gray-80${guide.hide_heading_anchors ? " hide-heading-anchors" : ""}`}
                dangerouslySetInnerHTML={{ __html: guide.html }}
              />
            </div>
          </article>
        ) : (
          /* ---- Single-column fallback (no TOC) ---- */
          <article className="mx-auto max-w-3xl">
            <h1 className="mb-3 font-display text-4xl font-bold leading-tight text-charcoal md:text-5xl">
              {guide.title}
            </h1>
            <GuideMeta guide={guide} />
            {guide.featured_image && (
              <img
                src={guide.featured_image}
                alt={guide.title}
                className="mb-8 w-full rounded-lg"
              />
            )}
            <AskAiSummary url={`/guides/${guide.slug}`} className="mb-8" />
            <div
              className={`content group${guide.hide_heading_anchors ? " hide-heading-anchors" : ""}`}
              dangerouslySetInnerHTML={{ __html: guide.html }}
            />
          </article>
        )}
      </div>
    </>
  );
}
